/**
 * Task Action View
 *
 * Action-oriented detail view for a WorkTask from the Task Inbox.
 * Shows entity context, outcome buttons, and entity navigation.
 * This is the primary user-facing view for acting on workflow tasks.
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Stack,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import OpenInNew from '@mui/icons-material/OpenInNew';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PanTool from '@mui/icons-material/PanTool';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { getErrorMessage } from '@/lib/apiClient';
import { checkOfflineAndNotify } from '@/lib/offlineGuard';
import { formatDateTime } from '@/lib/locale';
import { resolveEntityUrl } from '@/lib/entityNavigation';
import { useWorkTask } from '@/hooks/taskmanagement/useWorkTask';
import {
  useClaimTask,
  useCompleteTask,
  useUnclaimTask,
  useCancelTask,
} from '@/hooks/taskmanagement/useTaskInbox';

function parseOutcomes(availableOutcomes?: string): string[] {
  if (!availableOutcomes) return [];
  try {
    const parsed = JSON.parse(availableOutcomes);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseContextData(contextData?: string): Record<string, string> | null {
  if (!contextData) return null;
  try {
    const parsed = JSON.parse(contextData);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  PENDING: 'warning',
  CLAIMED: 'info',
  COMPLETED: 'success',
  CANCELLED: 'default',
  ESCALATED: 'error',
  EXPIRED: 'default',
};

const priorityColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  LOW: 'default',
  NORMAL: 'primary',
  HIGH: 'warning',
  URGENT: 'error',
};

export const TaskActionView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: task, isLoading } = useWorkTask(id!);

  const { t } = useTranslation();
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [comment, setComment] = useState('');
  const [apiErrors, setApiErrors] = useState<ValidationError[]>([]);

  const claimMutation = useClaimTask();
  const unclaimMutation = useUnclaimTask();
  const completeMutation = useCompleteTask();
  const cancelMutation = useCancelTask();

  const handleError = useCallback((error: unknown) => {
    const errors = getValidationErrors(error);
    if (errors.length > 0) {
      setApiErrors(errors);
    } else {
      setApiErrors([{
        field: 'form',
        message: getErrorMessage(error),
        instancePath: '#',
        members: [],
      }]);
    }
  }, []);

  const handleClaim = useCallback(() => {
    if (!id) return;
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    claimMutation.mutate(id, { onError: handleError });
  }, [id, claimMutation, handleError, t]);

  const handleUnclaim = useCallback(() => {
    if (!id) return;
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    unclaimMutation.mutate(id, { onError: handleError });
  }, [id, unclaimMutation, handleError, t]);

  const handleCancel = useCallback(() => {
    if (!id) return;
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    cancelMutation.mutate(id, { onError: handleError });
  }, [id, cancelMutation, handleError, t]);

  const handleComplete = useCallback(() => {
    if (!id || !selectedOutcome) return;
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    completeMutation.mutate(
      { id, input: { outcome: selectedOutcome, comment: comment || undefined } },
      { onError: handleError }
    );
  }, [id, selectedOutcome, comment, completeMutation, handleError, t]);

  const outcomes = useMemo(() => task ? parseOutcomes(task.availableOutcomes) : [], [task]);
  const contextData = useMemo(() => task ? parseContextData(task.contextData) : null, [task]);
  const entityUrl = useMemo(() => task ? resolveEntityUrl(task.entityName, task.entityId) : null, [task]);

  const isMutationCompleted = completeMutation.isSuccess || cancelMutation.isSuccess;
  const isActionable = task && !isMutationCompleted && (
    task.status === 'PENDING' || task.status === 'CLAIMED' || task.status === 'ESCALATED'
  );
  const isTerminal = task && (
    task.status === 'COMPLETED' || task.status === 'CANCELLED' || task.status === 'EXPIRED'
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!task) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Task not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <ViewToolbar onRefresh={() => {}} />

      <ValidationErrorBanner
        errors={apiErrors}
        onExpand={() => {}}
        onClear={() => setApiErrors([])}
      />

      <Box sx={{ p: 2, maxWidth: 800, mx: 'auto', width: '100%' }}>
        {/* Back button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/taskmanagement/inbox')}
          sx={{ mb: 2 }}
          size="small"
        >
          Back to Inbox
        </Button>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
            {task.title}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={task.status}
              color={statusColors[task.status] ?? 'default'}
              size="small"
            />
            <Chip
              label={task.priority}
              color={priorityColors[task.priority] ?? 'default'}
              size="small"
              variant="outlined"
            />
            {task.dueAt && (
              <Chip
                label={`Due: ${formatDateTime(new Date(task.dueAt))}`}
                color={task.isOverdue ? 'error' : 'default'}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>

        {/* Entity Context Card */}
        {(contextData || task.entityName) && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {task.entityName || 'Entity'} Details
              </Typography>
              {contextData && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: 0.5,
                    columnGap: 2,
                  }}
                >
                  {Object.entries(contextData).map(([label, value]) => (
                    value ? (
                      <Box key={label} sx={{ display: 'contents' }}>
                        <Typography variant="body2" color="text.secondary">
                          {label}:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {value}
                        </Typography>
                      </Box>
                    ) : null
                  ))}
                </Box>
              )}
            </CardContent>
            {entityUrl && (
              <CardActions>
                <Button
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() => navigate(entityUrl)}
                >
                  View {task.entityName || 'Entity'}
                </Button>
              </CardActions>
            )}
          </Card>
        )}

        {/* Completed/Cancelled Banner */}
        {isTerminal && (
          <Alert
            severity={task.status === 'COMPLETED' ? 'success' : 'info'}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              <strong>{task.status === 'COMPLETED' ? 'Completed' : task.status}</strong>
              {task.selectedOutcome && <> with outcome: <strong>{task.selectedOutcome}</strong></>}
              {task.completedAt && <> on {formatDateTime(new Date(task.completedAt))}</>}
            </Typography>
            {task.outcomeComment && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Comment: {task.outcomeComment}
              </Typography>
            )}
          </Alert>
        )}

        {/* Action Area */}
        {isActionable && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Actions
              </Typography>

              {/* Claim / Unclaim */}
              {task.status === 'PENDING' && (
                <Button
                  variant="outlined"
                  startIcon={<PanTool />}
                  onClick={handleClaim}
                  disabled={claimMutation.isPending}
                  sx={{ mb: 2 }}
                >
                  {claimMutation.isPending ? 'Claiming...' : 'Claim Task'}
                </Button>
              )}
              {task.status === 'CLAIMED' && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleUnclaim}
                  disabled={unclaimMutation.isPending}
                  sx={{ mb: 2 }}
                >
                  Release Task
                </Button>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Outcome selection */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select Outcome
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {outcomes.map((outcome) => (
                  <Chip
                    key={outcome}
                    label={outcome}
                    color={selectedOutcome === outcome ? 'primary' : 'default'}
                    variant={selectedOutcome === outcome ? 'filled' : 'outlined'}
                    onClick={() => setSelectedOutcome(outcome)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>

              {outcomes.length === 0 && (
                <TextField
                  label="Outcome"
                  value={selectedOutcome}
                  onChange={(e) => setSelectedOutcome(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                label="Comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={handleComplete}
                  disabled={!selectedOutcome || completeMutation.isPending}
                >
                  {completeMutation.isPending ? 'Completing...' : 'Complete Task'}
                </Button>
                {/* An approval task cannot be cancelled on its own — doing so would leave the
                    approval open with nobody holding it, so the server refuses. Calling the
                    approval off is Cancel on the ApprovalRequest, which tears its tasks down
                    with it. Hidden rather than shown-and-rejected. */}
                {!task.approvalRequestId && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleCancel}
                    disabled={cancelMutation.isPending}
                  >
                    Cancel Task
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Workflow Details (collapsible) */}
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Workflow Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 0.5,
                columnGap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">Workflow:</Typography>
              <Typography variant="body2">{task.workflowName}</Typography>
              <Typography variant="body2" color="text.secondary">Step:</Typography>
              <Typography variant="body2">{task.stepName}</Typography>
              <Typography variant="body2" color="text.secondary">Instance ID:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {task.workflowInstanceId}
              </Typography>
              <Typography variant="body2" color="text.secondary">Entity Type:</Typography>
              <Typography variant="body2">{task.entityType}</Typography>
              <Typography variant="body2" color="text.secondary">Entity ID:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {task.entityId}
              </Typography>
              {task.correlationId && (
                <>
                  <Typography variant="body2" color="text.secondary">Correlation ID:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {task.correlationId}
                  </Typography>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {task.description && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2">{task.description}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
